// Comms Book: shared component rendered on both the Admin Dashboard (full
// management: post, target by roster group, see who's read what) and the
// Driver Dashboard (read + acknowledge only). Which controls show is
// driven by isDispatchOrAdmin, but the real access boundary is the DB's
// own RLS -- see src/lib/commsBook.ts and the comms_book migrations.
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { Plus, Search, Archive, Star, Paperclip, X, Trash2, Users, Tags, Globe, Clock, Check, Pencil } from "lucide-react";
import {
  loadCommsCategories,
  loadRosterGroupOptions,
  loadCommsMessages,
  loadPendingCommsMessages,
  loadMyCommsMessages,
  createCommsMessage,
  updateCommsMessage,
  submitDriverCommsMessage,
  updateMyCommsMessage,
  approveCommsMessage,
  rejectCommsMessage,
  deleteCommsMessage,
  uploadCommsAttachment,
  deleteCommsAttachment,
  commsAttachmentUrl,
  markCommsMessageRead,
  type CommsCategory,
  type RosterGroupOption,
  type CommsMessage,
  type CommsAttachment,
} from "@/lib/commsBook";
import { formatDateTime } from "@/lib/dates";
import CommsCategoryManager from "@/components/CommsCategoryManager";

const EVERYONE = "Everyone";

/**
 * Belt-and-braces display filter -- NOT the security boundary (RLS is).
 * It exists because the Admin Dashboard's "Driver View Emulator" only
 * swaps which driver's name/props get rendered; it never re-authenticates
 * as that driver, so the real Supabase session behind it is still the
 * admin's own. RLS correctly reflects the REAL logged-in identity, which
 * means an admin previewing a driver's dashboard still gets an admin's
 * view of Comms Book. This filter re-applies the same audience rule
 * client-side using the driver actually being previewed, so the preview
 * looks like what that driver would really see. For a driver's own real
 * login this is a no-op -- RLS has already scoped `messages` correctly.
 */
function isVisibleToRosterGroups(m: CommsMessage, rosterGroups: string[]): boolean {
  if (!m.audience_groups || m.audience_groups.length === 0) return false; // Dispatch/Admin only
  if (m.audience_groups.includes(EVERYONE)) return true;
  return m.audience_groups.some((g) => rosterGroups.includes(g));
}

interface CommsBookProps {
  currentDriverName: string;
  isDispatchOrAdmin: boolean;
  /** The previewed driver's own roster groups -- see isVisibleToRosterGroups. */
  currentDriverRosterGroups?: string[] | null;
}

export default function CommsBook({ currentDriverName, isDispatchOrAdmin, currentDriverRosterGroups }: CommsBookProps) {
  const [messages, setMessages] = useState<CommsMessage[]>([]);
  const [categories, setCategories] = useState<CommsCategory[]>([]);
  const [rosterGroups, setRosterGroups] = useState<RosterGroupOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [pendingApprovalOpen, setPendingApprovalOpen] = useState(false);
  const [myMessagesOpen, setMyMessagesOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [myPendingCount, setMyPendingCount] = useState(0);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [expandedReads, setExpandedReads] = useState<Set<string>>(new Set());
  // Shared between ComposerDialog (admin) and SuggestMessageDialog (driver)
  // -- only one of the two is ever mounted at a time, so one slot is fine.
  const [editingMessage, setEditingMessage] = useState<CommsMessage | null>(null);

  const load = async () => {
    setLoading(true);
    const [msgs, cats, groups] = await Promise.all([
      loadCommsMessages({ includeArchived: showArchived, search }),
      loadCommsCategories(),
      loadRosterGroupOptions(),
    ]);
    setMessages(msgs);
    setCategories(cats);
    setRosterGroups(groups);
    // Regular drivers' RLS returns nothing for pending rows, so this is
    // effectively a no-op for them -- only fetch it for Dispatch/Admin.
    if (isDispatchOrAdmin) {
      setPendingCount((await loadPendingCommsMessages()).length);
    } else {
      const mine = await loadMyCommsMessages(currentDriverName);
      setMyPendingCount(mine.filter((m) => m.status === "pending").length);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const runSearch = () => load();

  const filteredMessages = useMemo(() => {
    let list = messages;
    if (!isDispatchOrAdmin) {
      list = list.filter((m) => isVisibleToRosterGroups(m, currentDriverRosterGroups || []));
    }
    if (categoryFilter !== "all") {
      list = list.filter((m) => m.category_id === categoryFilter);
    }
    return list;
  }, [messages, categoryFilter, isDispatchOrAdmin, currentDriverRosterGroups]);

  const handleRead = async (messageId: string) => {
    const ok = await markCommsMessageRead(messageId, currentDriverName);
    if (ok) load();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this message? This can't be undone.")) return;
    const ok = await deleteCommsMessage(id);
    if (ok) {
      toast({ title: "Message deleted" });
      load();
    } else {
      toast({ title: "Failed to delete message", variant: "destructive" });
    }
  };

  const handleEditMessage = (m: CommsMessage) => {
    setEditingMessage(m);
    setComposerOpen(true);
  };

  const closeComposer = (v: boolean) => {
    setComposerOpen(v);
    if (!v) setEditingMessage(null);
  };

  const closeSuggest = (v: boolean) => {
    setSuggestOpen(v);
    if (!v) setEditingMessage(null);
  };

  const handleEditMine = (m: CommsMessage) => {
    setMyMessagesOpen(false);
    setEditingMessage(m);
    setSuggestOpen(true);
  };

  const toggleReadsExpanded = (id: string) => {
    setExpandedReads((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search messages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              className="pl-8 w-64"
            />
          </div>
          <Button variant="outline" size="sm" onClick={runSearch}>
            Search
          </Button>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Checkbox id="comms-show-all" checked={showArchived} onCheckedChange={(c) => setShowArchived(!!c)} />
            <Label htmlFor="comms-show-all" className="text-sm cursor-pointer flex items-center gap-1">
              <Archive className="w-3.5 h-3.5" />
              Show All (incl. archived)
            </Label>
          </div>
        </div>

        {isDispatchOrAdmin ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setCategoryManagerOpen(true)}>
              <Tags className="w-4 h-4 mr-2" />
              Manage Categories
            </Button>
            <Button variant="outline" onClick={() => setPendingApprovalOpen(true)}>
              <Clock className="w-4 h-4 mr-2" />
              Pending Approval{pendingCount > 0 ? ` (${pendingCount})` : ""}
            </Button>
            <Button onClick={() => setComposerOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Message
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setMyMessagesOpen(true)}>
              <Clock className="w-4 h-4 mr-2" />
              My Messages{myPendingCount > 0 ? ` (${myPendingCount} pending)` : ""}
            </Button>
            <Button variant="outline" onClick={() => setSuggestOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Suggest a Message
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>
      ) : filteredMessages.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No messages here.</p>
      ) : (
        <div className="space-y-3">
          {filteredMessages.map((m) => {
            const hasRead = m.comms_message_reads.some((r) => r.driver_name === currentDriverName);
            const category = m.comms_categories;
            return (
              <div
                key={m.id}
                className={`rounded-lg border p-4 ${
                  m.is_important ? "border-yellow-500/60 bg-yellow-950/10" : "border-border bg-background/40"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      {m.is_important && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 shrink-0" />}
                      {m.title && <h4 className="font-semibold text-foreground">{m.title}</h4>}
                      {category && (
                        <Badge variant="secondary" style={category.color_hex ? { backgroundColor: category.color_hex } : undefined}>
                          {category.name}
                        </Badge>
                      )}
                      {m.audience_groups?.includes(EVERYONE) ? (
                        <Badge variant="outline" className="gap-1">
                          <Globe className="w-3 h-3" />
                          Everyone
                        </Badge>
                      ) : m.audience_groups && m.audience_groups.length > 0 ? (
                        <Badge variant="outline" className="gap-1">
                          <Users className="w-3 h-3" />
                          {m.audience_groups.join(", ")}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1 text-muted-foreground">
                          <Users className="w-3 h-3" />
                          Dispatch/Admin Only
                        </Badge>
                      )}
                      {m.archived_at && (
                        <Badge variant="outline" className="text-muted-foreground">
                          Archived
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{m.body}</p>

                    {m.comms_message_attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {m.comms_message_attachments.map((a) => (
                          <a key={a.id} href={commsAttachmentUrl(a.storage_path)} target="_blank" rel="noreferrer">
                            <img
                              src={commsAttachmentUrl(a.storage_path)}
                              alt="Attachment"
                              className="h-20 w-20 object-cover rounded-md border border-border hover:opacity-80"
                            />
                          </a>
                        ))}
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground mt-2">
                      {m.posted_by} · {formatDateTime(m.created_at)}
                    </p>
                  </div>

                  {isDispatchOrAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleEditMessage(m)} title="Edit message">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(m.id)} title="Delete message">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Checkbox id={`read-${m.id}`} checked={hasRead} disabled={hasRead} onCheckedChange={() => !hasRead && handleRead(m.id)} />
                    <Label htmlFor={`read-${m.id}`} className="text-xs cursor-pointer">
                      I have read and understood this message
                    </Label>
                  </div>

                  {isDispatchOrAdmin && (
                    <button
                      type="button"
                      onClick={() => toggleReadsExpanded(m.id)}
                      className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                    >
                      {m.comms_message_reads.length} read{m.comms_message_reads.length === 1 ? "" : "s"}
                    </button>
                  )}
                </div>

                {isDispatchOrAdmin && expandedReads.has(m.id) && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    {m.comms_message_reads.length === 0 ? (
                      "No one has acknowledged this yet."
                    ) : (
                      <ul className="space-y-0.5">
                        {m.comms_message_reads.map((r) => (
                          <li key={r.driver_name}>
                            {r.driver_name} — {formatDateTime(r.read_at)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isDispatchOrAdmin ? (
        <>
          <ComposerDialog
            open={composerOpen}
            onOpenChange={closeComposer}
            categories={categories}
            rosterGroups={rosterGroups}
            postedBy={currentDriverName}
            editingMessage={editingMessage}
            onPosted={load}
          />
          <CommsCategoryManager open={categoryManagerOpen} onOpenChange={setCategoryManagerOpen} onChanged={load} />
          <PendingApprovalDialog
            open={pendingApprovalOpen}
            onOpenChange={setPendingApprovalOpen}
            rosterGroups={rosterGroups}
            onChanged={load}
          />
        </>
      ) : (
        <>
          <SuggestMessageDialog
            open={suggestOpen}
            onOpenChange={closeSuggest}
            categories={categories}
            postedBy={currentDriverName}
            editingMessage={editingMessage}
            onSubmitted={load}
          />
          <MyMessagesDialog
            open={myMessagesOpen}
            onOpenChange={setMyMessagesOpen}
            postedBy={currentDriverName}
            onEdit={handleEditMine}
          />
        </>
      )}
    </div>
  );
}

/** Mutually-exclusive audience toggle shared by the composer and the
 *  approval panel: "Everyone" and specific roster groups can't combine. */
function toggleAudienceGroup(prev: string[], name: string): string[] {
  if (name === EVERYONE) return prev.includes(EVERYONE) ? [] : [EVERYONE];
  const withoutEveryone = prev.filter((g) => g !== EVERYONE);
  return withoutEveryone.includes(name) ? withoutEveryone.filter((g) => g !== name) : [...withoutEveryone, name];
}

function AudiencePicker({
  rosterGroups,
  selectedGroups,
  onToggle,
}: {
  rosterGroups: RosterGroupOption[];
  selectedGroups: string[];
  onToggle: (name: string) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onToggle(EVERYONE)}
          className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors flex items-center gap-1 ${
            selectedGroups.includes(EVERYONE)
              ? "bg-blue-500 border-blue-500 text-white"
              : "border-border bg-background/60 text-muted-foreground hover:bg-background"
          }`}
        >
          <Globe className="w-3 h-3" />
          Everyone
        </button>
        {rosterGroups.map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => onToggle(g.name)}
            className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
              selectedGroups.includes(g.name)
                ? "bg-yellow-500 border-yellow-500 text-black"
                : "border-border bg-background/60 text-muted-foreground hover:bg-background"
            }`}
          >
            {g.name}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {selectedGroups.length === 0
          ? "Nothing selected -- visible to Dispatch/Admin only."
          : selectedGroups.includes(EVERYONE)
          ? "Visible to everyone."
          : `Only visible to: ${selectedGroups.join(", ")} (plus Dispatch/Admin).`}
      </p>
    </div>
  );
}

function ComposerDialog({
  open,
  onOpenChange,
  categories,
  rosterGroups,
  postedBy,
  editingMessage,
  onPosted,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  categories: CommsCategory[];
  rosterGroups: RosterGroupOption[];
  postedBy: string;
  editingMessage?: CommsMessage | null;
  onPosted: () => void;
}) {
  const [categoryId, setCategoryId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [isImportant, setIsImportant] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<CommsAttachment[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCategoryId("");
    setTitle("");
    setBody("");
    setIsImportant(false);
    setSelectedGroups([]);
    setExistingAttachments([]);
    setFiles([]);
  };

  useEffect(() => {
    if (!open) return;
    if (editingMessage) {
      setCategoryId(editingMessage.category_id || "");
      setTitle(editingMessage.title || "");
      setBody(editingMessage.body);
      setIsImportant(editingMessage.is_important);
      setSelectedGroups(editingMessage.audience_groups || []);
      setExistingAttachments(editingMessage.comms_message_attachments);
      setFiles([]);
    } else {
      reset();
    }
  }, [open, editingMessage]);

  const toggleGroup = (name: string) => setSelectedGroups((prev) => toggleAudienceGroup(prev, name));

  const handleRemoveExisting = async (a: CommsAttachment) => {
    await deleteCommsAttachment(a.id, a.storage_path);
    setExistingAttachments((prev) => prev.filter((x) => x.id !== a.id));
  };

  const handlePost = async () => {
    if (!body.trim()) {
      toast({ title: "Message can't be empty", variant: "destructive" });
      return;
    }
    setSaving(true);
    const audienceGroups = selectedGroups.length > 0 ? selectedGroups : null;
    const messageId = editingMessage
      ? ((await updateCommsMessage(editingMessage.id, {
          categoryId: categoryId || null,
          audienceGroups,
          title: title.trim() || null,
          body: body.trim(),
          isImportant,
        }))
          ? editingMessage.id
          : null)
      : await createCommsMessage({
          categoryId: categoryId || null,
          audienceGroups,
          title: title.trim() || null,
          body: body.trim(),
          isImportant,
          postedBy,
        });
    if (!messageId) {
      toast({ title: editingMessage ? "Failed to save message" : "Failed to post message", variant: "destructive" });
      setSaving(false);
      return;
    }
    for (const file of files) {
      await uploadCommsAttachment(messageId, file);
    }
    setSaving(false);
    toast({ title: editingMessage ? "Message updated" : "Message posted" });
    reset();
    onOpenChange(false);
    onPosted();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingMessage ? "Edit Message" : "New Comms Book Message"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Uncategorised" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Visible To</Label>
            <AudiencePicker rosterGroups={rosterGroups} selectedGroups={selectedGroups} onToggle={toggleGroup} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="comms-title">Title (optional)</Label>
            <Input id="comms-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short heading" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="comms-body">Message</Label>
            <Textarea id="comms-body" value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="What do you need everyone to know?" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="comms-attachments" className="flex items-center gap-2">
              <Paperclip className="w-4 h-4" /> Screenshots / Attachments
            </Label>
            {existingAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {existingAttachments.map((a) => (
                  <div key={a.id} className="relative">
                    <img
                      src={commsAttachmentUrl(a.storage_path)}
                      alt="Attachment"
                      className="h-16 w-16 object-cover rounded-md border border-border"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveExisting(a)}
                      className="absolute -top-1.5 -right-1.5 bg-background border border-border rounded-full p-0.5 hover:bg-destructive hover:text-destructive-foreground"
                      title="Remove attachment"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Input
              id="comms-attachments"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    {f.name}
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="comms-important" checked={isImportant} onCheckedChange={(c) => setIsImportant(!!c)} />
            <Label htmlFor="comms-important">Mark as Important (highlighted)</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handlePost} disabled={saving}>
            {saving ? (editingMessage ? "Saving..." : "Posting...") : editingMessage ? "Save Changes" : "Post Message"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Driver-facing composer -- no audience picker, no "Important" flag; both
 *  are set by Dispatch/Admin at approval time. Always lands as Pending. */
function SuggestMessageDialog({
  open,
  onOpenChange,
  categories,
  postedBy,
  editingMessage,
  onSubmitted,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  categories: CommsCategory[];
  postedBy: string;
  editingMessage?: CommsMessage | null;
  onSubmitted: () => void;
}) {
  const [categoryId, setCategoryId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [existingAttachments, setExistingAttachments] = useState<CommsAttachment[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCategoryId("");
    setTitle("");
    setBody("");
    setExistingAttachments([]);
    setFiles([]);
  };

  useEffect(() => {
    if (!open) return;
    if (editingMessage) {
      setCategoryId(editingMessage.category_id || "");
      setTitle(editingMessage.title || "");
      setBody(editingMessage.body);
      setExistingAttachments(editingMessage.comms_message_attachments);
      setFiles([]);
    } else {
      reset();
    }
  }, [open, editingMessage]);

  const handleRemoveExisting = async (a: CommsAttachment) => {
    await deleteCommsAttachment(a.id, a.storage_path);
    setExistingAttachments((prev) => prev.filter((x) => x.id !== a.id));
  };

  const handleSubmit = async () => {
    if (!body.trim()) {
      toast({ title: "Message can't be empty", variant: "destructive" });
      return;
    }
    setSaving(true);
    const messageId = editingMessage
      ? ((await updateMyCommsMessage(editingMessage.id, {
          categoryId: categoryId || null,
          title: title.trim() || null,
          body: body.trim(),
        }))
          ? editingMessage.id
          : null)
      : await submitDriverCommsMessage({
          categoryId: categoryId || null,
          title: title.trim() || null,
          body: body.trim(),
          postedBy,
        });
    if (!messageId) {
      toast({ title: editingMessage ? "Failed to save message" : "Failed to submit message", variant: "destructive" });
      setSaving(false);
      return;
    }
    for (const file of files) {
      await uploadCommsAttachment(messageId, file);
    }
    setSaving(false);
    toast({ title: "Sent for approval", description: "Dispatch/Admin will review it before other drivers can see it." });
    reset();
    onOpenChange(false);
    onSubmitted();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingMessage ? "Edit Your Message" : "Suggest a Message"}</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground -mt-2">
          {editingMessage
            ? "Saving will send this back to Dispatch/Admin for approval before others can see it."
            : "Dispatch/Admin will review this before other drivers can see it."}
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Uncategorised" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="comms-suggest-title">Title (optional)</Label>
            <Input id="comms-suggest-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short heading" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="comms-suggest-body">Message</Label>
            <Textarea
              id="comms-suggest-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="What do you want to tell everyone?"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="comms-suggest-attachments" className="flex items-center gap-2">
              <Paperclip className="w-4 h-4" /> Screenshots / Attachments
            </Label>
            {existingAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {existingAttachments.map((a) => (
                  <div key={a.id} className="relative">
                    <img
                      src={commsAttachmentUrl(a.storage_path)}
                      alt="Attachment"
                      className="h-16 w-16 object-cover rounded-md border border-border"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveExisting(a)}
                      className="absolute -top-1.5 -right-1.5 bg-background border border-border rounded-full p-0.5 hover:bg-destructive hover:text-destructive-foreground"
                      title="Remove attachment"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Input
              id="comms-suggest-attachments"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    {f.name}
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : editingMessage ? "Save Changes" : "Submit for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Dispatch/Admin-only queue of driver-suggested messages. RLS already
 *  restricts loadPendingCommsMessages to Dispatch/Admin, so a regular
 *  driver would just see an empty list even if this ever rendered for one. */
function PendingApprovalDialog({
  open,
  onOpenChange,
  rosterGroups,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  rosterGroups: RosterGroupOption[];
  onChanged: () => void;
}) {
  const [pending, setPending] = useState<CommsMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [isImportant, setIsImportant] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setPending(await loadPendingCommsMessages());
    setLoading(false);
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const startApprove = (id: string) => {
    setApprovingId(id);
    setSelectedGroups([]);
    setIsImportant(false);
  };

  const toggleGroup = (name: string) => setSelectedGroups((prev) => toggleAudienceGroup(prev, name));

  const confirmApprove = async (id: string) => {
    setSaving(true);
    const ok = await approveCommsMessage(id, selectedGroups.length > 0 ? selectedGroups : null, isImportant);
    setSaving(false);
    if (ok) {
      toast({ title: "Message approved" });
      setApprovingId(null);
      load();
      onChanged();
    } else {
      toast({ title: "Failed to approve message", variant: "destructive" });
    }
  };

  const handleReject = async (id: string) => {
    if (!window.confirm("Reject this suggested message? It won't be shown to anyone.")) return;
    const ok = await rejectCommsMessage(id);
    if (ok) {
      toast({ title: "Message rejected" });
      load();
      onChanged();
    } else {
      toast({ title: "Failed to reject message", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Pending Approval</DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>
        ) : pending.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Nothing waiting on approval.</p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {pending.map((m) => (
              <div key={m.id} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {m.title && <h4 className="font-semibold text-foreground">{m.title}</h4>}
                  {m.comms_categories && (
                    <Badge
                      variant="secondary"
                      style={m.comms_categories.color_hex ? { backgroundColor: m.comms_categories.color_hex } : undefined}
                    >
                      {m.comms_categories.name}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap">{m.body}</p>
                {m.comms_message_attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {m.comms_message_attachments.map((a) => (
                      <a key={a.id} href={commsAttachmentUrl(a.storage_path)} target="_blank" rel="noreferrer">
                        <img
                          src={commsAttachmentUrl(a.storage_path)}
                          alt="Attachment"
                          className="h-16 w-16 object-cover rounded-md border border-border hover:opacity-80"
                        />
                      </a>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Suggested by {m.posted_by} · {formatDateTime(m.created_at)}
                </p>

                {approvingId === m.id ? (
                  <div className="space-y-3 pt-2 border-t border-border/50">
                    <div className="space-y-2">
                      <Label>Visible To</Label>
                      <AudiencePicker rosterGroups={rosterGroups} selectedGroups={selectedGroups} onToggle={toggleGroup} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id={`approve-important-${m.id}`} checked={isImportant} onCheckedChange={(c) => setIsImportant(!!c)} />
                      <Label htmlFor={`approve-important-${m.id}`}>Mark as Important (highlighted)</Label>
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setApprovingId(null)}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={() => confirmApprove(m.id)} disabled={saving}>
                        {saving ? "Approving..." : "Confirm Approve"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 justify-end pt-1">
                    <Button variant="outline" size="sm" onClick={() => handleReject(m.id)}>
                      <X className="w-3.5 h-3.5 mr-1" />
                      Reject
                    </Button>
                    <Button size="sm" onClick={() => startApprove(m.id)}>
                      <Check className="w-3.5 h-3.5 mr-1" />
                      Approve
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** A driver's own submissions across every status, so they can see
 *  whether something they posted is pending/approved/rejected instead of
 *  wondering if it silently failed -- and edit it from here. Editing
 *  always sends it back to pending (see updateMyCommsMessage). */
function MyMessagesDialog({
  open,
  onOpenChange,
  postedBy,
  onEdit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  postedBy: string;
  onEdit: (m: CommsMessage) => void;
}) {
  const [mine, setMine] = useState<CommsMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setMine(await loadMyCommsMessages(postedBy));
    setLoading(false);
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const statusBadge = (status: CommsMessage["status"]) => {
    if (status === "approved") {
      return (
        <Badge variant="outline" className="gap-1 text-green-500 border-green-500/50">
          <Check className="w-3 h-3" />
          Approved
        </Badge>
      );
    }
    if (status === "rejected") {
      return (
        <Badge variant="outline" className="gap-1 text-red-400 border-red-400/50">
          <X className="w-3 h-3" />
          Rejected
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="gap-1 text-yellow-500 border-yellow-500/50">
        <Clock className="w-3 h-3" />
        Pending
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>My Messages</DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>
        ) : mine.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">You haven't suggested any messages yet.</p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {mine.map((m) => (
              <div key={m.id} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    {m.title && <h4 className="font-semibold text-foreground">{m.title}</h4>}
                    {m.comms_categories && (
                      <Badge
                        variant="secondary"
                        style={m.comms_categories.color_hex ? { backgroundColor: m.comms_categories.color_hex } : undefined}
                      >
                        {m.comms_categories.name}
                      </Badge>
                    )}
                  </div>
                  {statusBadge(m.status)}
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap">{m.body}</p>
                {m.comms_message_attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {m.comms_message_attachments.map((a) => (
                      <img
                        key={a.id}
                        src={commsAttachmentUrl(a.storage_path)}
                        alt="Attachment"
                        className="h-16 w-16 object-cover rounded-md border border-border"
                      />
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between pt-1">
                  <p className="text-xs text-muted-foreground">{formatDateTime(m.created_at)}</p>
                  <Button variant="outline" size="sm" onClick={() => onEdit(m)}>
                    <Pencil className="w-3.5 h-3.5 mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
